import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Loader2, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/pos/ErrorState';
import { logoutSession } from '@/api/auth.api';
import { OpenShiftPanel } from '@/components/pos/OpenShiftPanel';
import { useShiftStatus } from '@/hooks/queries';
import { isFocOnly } from '@/lib/permissions';
import { useAuthStore } from '@/stores/auth.store';

/**
 * The one page a cashier may stay on with no shift: the close-shift page, so the
 * result of the close they just made (and its print) stays on screen. Anything
 * else — the Shift page included — shows the opening screen until a shift is
 * open; the close-shift page itself sends a cashier with nothing just closed away.
 */
const OPEN_WITHOUT_SHIFT = ['/shift/close'];

/**
 * A cashier opens a shift before anything else. The server decides who must
 * (`GET /sale/shift/status` → `required`: the cashier roles) and refuses their
 * sales without one (409 SHIFT_NOT_OPEN); this makes the opening the first
 * screen after login instead of a refusal at checkout. An FOC-only account
 * takes no money and is never asked.
 */
export function RequireOpenShift() {
  const location = useLocation();
  const focOnly = useAuthStore((s) => isFocOnly({ permissions: s.permissions, wildcards: s.wildcards }));
  const exempt = focOnly || OPEN_WITHOUT_SHIFT.includes(location.pathname);
  const { data, isLoading, isError, error, refetch } = useShiftStatus(!focOnly);

  if (exempt) return <Outlet />;
  if (isLoading) {
    return (
      <div
        className="flex h-screen w-screen items-center justify-center bg-background text-ink-400"
        role="status"
        aria-label="Memeriksa shift"
      >
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  if (isError) {
    return (
      <ShiftScreen>
        <ErrorState
          title="Status shift belum dimuat"
          message={(error as { message?: string } | null)?.message ?? 'Gagal memuat shift.'}
          onRetry={() => void refetch()}
        />
      </ShiftScreen>
    );
  }
  if (data?.required && !data.shift) return <OpenShiftScreen />;
  return <Outlet />;
}

function ShiftScreen({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md space-y-3">
        {children}
        <Button
          variant="ghost"
          className="w-full gap-2"
          onClick={async () => {
            await logoutSession(useAuthStore.getState().refreshToken);
            logout();
            navigate('/login', { replace: true });
          }}
        >
          <LogOut className="h-4 w-4" /> Keluar
        </Button>
      </div>
    </div>
  );
}

function OpenShiftScreen() {
  const user = useAuthStore((s) => s.user);
  return (
    <ShiftScreen>
      <div className="space-y-4 rounded-xl border border-cream-200 bg-white p-6">
        <div>
          <h1 className="font-display text-xl font-semibold text-ink-900">Buka shift</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {user?.name ? `Halo, ${user.name}. ` : ''}Buka shift dulu sebelum berjualan. Modal awal diatur oleh Finance.
          </p>
        </div>
        <OpenShiftPanel />
      </div>
    </ShiftScreen>
  );
}
